// SubjectPublicFormModal.jsx
import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { generatePublicFormLink, disablePublicFormLink } from '../../Requests/SubjectRequests';

const SubjectPublicFormModal = ({ handleClose, project, t }) => {
    const [isPublic, setIsPublic] = useState(project.public_form?.is_enabled || false);
    const [loading, setLoading] = useState(false);
    const [publicLink, setPublicLink] = useState(project.public_form?.public_link || '');
    const [password, setPassword] = useState(project.public_form?.password || '');
    const [title, setTitle] = useState(project.public_form?.title || '');
    const [description, setDescription] = useState(project.public_form?.description || '');
    const [copied, setCopied] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    useEffect(() => {
        if (project.public_form?.is_enabled) {
            setIsPublic(true);
            setPublicLink(project.public_form.public_link);
            setPassword(project.public_form.password || '');
            setTitle(project.public_form.title || '');
            setDescription(project.public_form.description || '');
        }
    }, [project]);

    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await generatePublicFormLink({
                project_id: project.id,
                password: password || null,
                title: title || null,
                description: description || null
            });

            if (response.data) {
                setPublicLink(response.data.public_link);
                setUnsavedChanges(false);
            }
        } catch (error) {
            console.error('Error generating link:', error);
        }
        setLoading(false);
    };

    const handleTogglePublic = async (enabled) => {
        if (!enabled) {
            setLoading(true);
            try {
                await disablePublicFormLink({
                    project_id: project.id
                });
                setPublicLink('');
                setPassword('');
                setTitle('');
                setDescription('');
                setIsPublic(false);
                setUnsavedChanges(false);
            } catch (error) {
                console.error('Error disabling public link:', error);
            }
            setLoading(false);
        } else {
            setIsPublic(true);
            setUnsavedChanges(true);
            if (!publicLink) {
                handleSave();
            }
        }
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
        setUnsavedChanges(true);
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(publicLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <Dialog open={true} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg max-w-lg w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                        <Dialog.Title className="text-lg font-medium">
                            {t('SubjectProperties.PublicFormSettings')}
                        </Dialog.Title>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">
                                    {t('SubjectProperties.PublicFormAccess')}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {t('SubjectProperties.PublicFormDescription')}
                                </p>
                            </div>
                            <Switch
                                checked={isPublic}
                                onChange={handleTogglePublic}
                                disabled={loading}
                                className={`${isPublic ? 'bg-black' : 'bg-gray-200'
                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                            >
                                <span className="sr-only">Enable public form</span>
                                <span
                                    className={`${isPublic ? 'translate-x-6' : 'translate-x-1'
                                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                />
                            </Switch>
                        </div>

                        {isPublic && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('SubjectProperties.FormTitle')}
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={handleInputChange(setTitle)}
                                        placeholder={t('SubjectProperties.FormTitlePlaceholder')}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('SubjectProperties.FormDescription')}
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={handleInputChange(setDescription)}
                                        placeholder={t('SubjectProperties.FormDescriptionPlaceholder')}
                                        rows={3}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('SubjectProperties.Password')}
                                    </label>
                                    <input
                                        type="text"
                                        value={password}
                                        onChange={handleInputChange(setPassword)}
                                        placeholder={t('SubjectProperties.PasswordPlaceholder')}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                    />
                                    <p className="mt-1 text-sm text-gray-500">
                                        {t('SubjectProperties.PasswordHint')}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('SubjectProperties.PublicLink')}
                                    </label>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                                        <input
                                            type="text"
                                            readOnly
                                            value={publicLink}
                                            className="flex-1 bg-transparent border-0 focus:ring-0 text-sm"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            {copied ? (
                                                <CheckIcon className="h-5 w-5 text-green-500" />
                                            ) : (
                                                <ClipboardIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {unsavedChanges && (
                                    <div className="flex justify-end pt-4 border-t">
                                        <button
                                            onClick={handleSave}
                                            disabled={loading}
                                            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${loading
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-black hover:bg-gray-800'
                                                }`}
                                        >
                                            {loading ? t('Common.Saving') : t('Common.Save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default SubjectPublicFormModal;
